import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { formatPrice } from '../utils/priceFormatter';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TransactionCard = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 1.5rem;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const ArtworkImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
`;

const TransactionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ArtworkTitle = styled.h3`
  font-size: 1.2rem;
  margin: 0;
`;

const ArtistName = styled.p`
  color: #666;
  margin: 0;
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #666;
  
  &.total {
    font-weight: bold;
    color: #000;
    margin-top: 0.25rem;
    padding-top: 0.25rem;
    border-top: 1px solid #eee;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: red;
`;

const PurchasesScreen = ({ user }) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    const fetchTransactions = async () => {
      try {
        const transactionsRef = collection(db, 'transactions');
        const q = query(
          transactionsRef,
          where('purchaser.id', '==', user.id),
          orderBy('timestamp', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const transactionData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setTransactions(transactionData);
      } catch (err) {
        console.error('Error fetching transactions:', err);
        setError('Failed to load purchase history');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  if (loading) {
    return <LoadingMessage>Loading purchase history...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  if (transactions.length === 0) {
    return (
      <Container>
        <Title>Purchase History</Title>
        <EmptyMessage>You haven't made any purchases yet.</EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Purchase History</Title>
      <TransactionList>
        {transactions.map(transaction => (
          <TransactionCard key={transaction.id}>
            <ArtworkImage 
              src={transaction.artwork.imageThumbnail || transaction.artwork.imageOriginal} 
              alt={transaction.artwork.title} 
            />
            <TransactionInfo>
              <ArtworkTitle>{transaction.artwork.title}</ArtworkTitle>
              <ArtistName>by {transaction.artist.displayName}</ArtistName>
              <PriceInfo>
                <PriceRow>
                  <span>Price:</span>
                  <span>{formatPrice(transaction.amounts?.preTaxAmount || transaction.price || 0)}</span>
                </PriceRow>
                <PriceRow>
                  <span>Tax:</span>
                  <span>{formatPrice(transaction.amounts?.taxAmount || 0)}</span>
                </PriceRow>
                <PriceRow className="total">
                  <span>Total:</span>
                  <span>{formatPrice(transaction.amounts?.totalAmount || transaction.price || 0)}</span>
                </PriceRow>
              </PriceInfo>
            </TransactionInfo>
          </TransactionCard>
        ))}
      </TransactionList>
    </Container>
  );
};

export default PurchasesScreen; 